import {
  IconButton,
  ListItem,
  ListItemText,
  List,
  Divider,
  MenuItem,
  Menu,
  Tooltip,
} from "@mui/material";
import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { TextField } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatIcon from "@mui/icons-material/Chat";
import { styled, useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/auth-context";
import { getData, postData } from "../helper/api";
import { v4 as uuidv4 } from "uuid";
import ConfirmDialog from "./confirmation";
import { useLoading } from "../hooks/loadingcontext";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

export type WorksheetMethods = {
  worksheetCreateMethod: () => void;
};

type WorksheetProps = {
  handleDrawerClose: () => void;
};

const Worksheets = forwardRef<WorksheetMethods, WorksheetProps>(
  ({ handleDrawerClose }, ref) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { state } = useAuthContext();

    const [items, setItems] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [tempValues, setTempValues] = useState({});
    const [selectedId, setSelectedId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuItem, setMenuItem] = useState(null);
    const inputRef = useRef(null);
    const effectRun = useRef(false);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] =
      useState<boolean>(false);

    const { isLoading, setIsLoading } = useLoading();

    useImperativeHandle(ref, () => ({
      worksheetCreateMethod: () => {
        createNewWorksheet();
      },
    }));

    useEffect(() => {
      // this avoids multiple use effect calls in strict mode
      if (!effectRun.current) {
        effectRun.current = true;
        handleLoad();
      }
    }, []);

    // Focus input only when renaming starts
    useEffect(() => {
      if (editingId !== null && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(0, inputRef.current.value.length);
      }
    }, [editingId]);

    const handleLoad = async () => {
      var userWorksheets = await getData(`worksheet`);

      // if no worksheets create new for the user
      if (userWorksheets === null || userWorksheets.length < 1) {
        await createNewWorksheet();
      } else {
        setItems(userWorksheets);
        setSelectedId(userWorksheets[0].id);
        navigate(`/ws/${userWorksheets[0].id}`);
      }
    };

    // Open menu
    const handleMenuOpen = (event, item) => {
      setAnchorEl(event.currentTarget);
      setMenuItem(item);
    };

    // Close menu
    const handleMenuClose = () => {
      setAnchorEl(null);
      setMenuItem(null);
    };

    // Start renaming process
    const handleRenameClick = () => {
      setEditingId(menuItem.id);
      setTempValues((prev) => ({ ...prev, [menuItem.id]: menuItem.name }));
      handleMenuClose();
    };

    // Handle input change
    const handleInputChange = (event, id) => {
      setTempValues((prev) => ({ ...prev, [id]: event.target.value }));
    };

    // Save edited value
    const handleSave = async (id) => {
      const updatedItems = items.map((item) =>
        item.id === id ? { ...item, name: tempValues[id] || item.name } : item
      );
      setItems(updatedItems);
      setEditingId(null);
      const renamedWorksheet = updatedItems.find((x) => x.id === id);

      if (renamedWorksheet === null) {
        return;
      }

      renamedWorksheet.modifiedDate = new Date().toISOString();
      renamedWorksheet.userId = null;

      await postData(renamedWorksheet, `worksheet/update`);
    };

    // Delete item
    const handleDeleteClick = async () => {
      setItems(items.filter((item) => item.id !== menuItem.id));
      handleMenuClose();

      const deleteItem = items.find((item) => item.id === menuItem.id);

      if (deleteItem === null) {
        return;
      }

      deleteItem.modifiedDate = new Date().toISOString();
      deleteItem.isActive = false;
      deleteItem.userId = null;

      await postData(deleteItem, `worksheet/update`);
      await handleLoad();
    };

    // Handle selection
    const handleSelect = (id) => {
      if (isLoading) {
        return;
      }
      setSelectedId(id === selectedId ? null : id); // Toggle selection
      navigate(`/ws/${id}`);
    };

    const createNewWorksheet = async () => {
      const nowUtc = new Date().toISOString();
      var p = {
        id: uuidv4(),
        name: nowUtc,
        isActive: true,
        createdDate: nowUtc,
      };

      var response = await postData(p, `worksheet/insert`);
      if (response !== null && response.length > 0) {
        setItems(response);
        setSelectedId(response[0].id);
        navigate(`/ws/${response[0].id}`);
      }
    };

    return (
      <>
        <DrawerHeader>
          <Tooltip title="New Chat">
            <IconButton
              aria-label="newchat"
              size="medium"
              sx={{
                position: "absolute",
                left: 10,
              }}
              onClick={createNewWorksheet}
              disabled={isLoading}
            >
              <ChatIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>

          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {items.map((item) => (
            <ListItem
              key={item.id}
              disablePadding
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor:
                  selectedId === item.id ? "lightgray" : "transparent", // Highlight selected item
                borderRadius: "8px",
                p: 1,
                cursor: "pointer",
                maxHeight: "40px",
              }}
              onClick={() => handleSelect(item.id)} // Click to select
            >
              {editingId === item.id ? (
                <TextField
                  inputRef={inputRef}
                  value={tempValues[item.id] || item.name}
                  onChange={(event) => handleInputChange(event, item.id)}
                  onBlur={() => handleSave(item.id)}
                  onKeyDown={(event) =>
                    event.key === "Enter" && handleSave(item.id)
                  }
                  size="small"
                  fullWidth
                />
              ) : (
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    noWrap: true, // Prevents text from wrapping
                    sx: {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "200px", // Adjust as needed
                      fontSize: "14px",
                    },
                  }}
                />
              )}

              {/* Menu Button */}

              <IconButton
                onClick={(event) => handleMenuOpen(event, item)}
                size="small"
                disabled={isLoading}
              >
                <MoreVertIcon />
              </IconButton>
            </ListItem>
          ))}

          {/* Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleRenameClick}>
              <EditIcon sx={{ marginRight: 1 }} fontSize="small" />
              Rename
            </MenuItem>

            <MenuItem
              onClick={() => {
                setOpenDeleteConfirmation(true);
              }}
            >
              <DeleteIcon sx={{ marginRight: 1 }} fontSize="small" />
              Delete
            </MenuItem>
          </Menu>
        </List>
        <ConfirmDialog
          title="Confirmation?"
          open={openDeleteConfirmation}
          handleClose={() => setOpenDeleteConfirmation(false)}
          onConfirm={() => handleDeleteClick()}
        >
          Are you sure you want to delete?
        </ConfirmDialog>
      </>
    );
  }
);

export default Worksheets;
